import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Copy, KeyRound, Radio, RefreshCw, Unplug, Webhook, X } from "lucide-react";
import { cx, formatDate } from "../../utils";
import AppIcon from "../ui/AppIcon";
import Skeleton from "../ui/Skeleton";
import { useToast } from "../ui/Toast";

/**
 * Pieces shared by the Installs, Companies, Locations and Users pages, so an
 * install reads the same wherever it shows up.
 */

export const ago = (value) => {
  if (!value) return "";
  const s = (Date.now() - new Date(value).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return formatDate(value);
};


export const Pill = ({ tone = "ink", icon: Icon, children, title }) => (
  <span
    title={title}
    className={cx(
      "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1",
      tone === "emerald" && "bg-emerald-50 text-emerald-700 ring-emerald-200/80",
      tone === "amber" && "bg-amber-50 text-amber-700 ring-amber-200/80",
      tone === "rose" && "bg-rose-50 text-rose-700 ring-rose-200/80",
      tone === "brand" && "bg-brand-50 text-brand-700 ring-brand-200/70",
      tone === "violet" && "bg-violet-50 text-violet-700 ring-violet-200/70",
      tone === "ink" && "bg-ink-100 text-ink-600 ring-ink-200/80",
    )}
  >
    {Icon && <Icon size={11} aria-hidden />}
    {children}
  </span>
);

export const StatusPill = ({ install }) =>
  install.active ? (
    <Pill tone="emerald" icon={CheckCircle2}>Installed</Pill>
  ) : (
    <Pill tone="ink" icon={Unplug}>Uninstalled</Pill>
  );

export const WebhookPill = ({ install }) =>
  install.webhookVerifiedAt ? (
    <Pill tone="emerald" icon={Radio} title={`First message received ${formatDate(install.webhookVerifiedAt)}`}>Receiving</Pill>
  ) : install.webhookConfirmedAt ? (
    <Pill tone="amber" icon={Webhook} title="The customer ticked the webhook step, but no message has arrived yet">Confirmed</Pill>
  ) : (
    <Pill tone="ink" icon={Webhook}>No webhook</Pill>
  );

export const CredentialsPill = ({ install }) => {
  const { filled, total } = install.credentials || {};
  if (!total) return <Pill tone="ink" icon={KeyRound}>No fields</Pill>;
  if (filled === total) return <Pill tone="emerald" icon={KeyRound}>Credentials</Pill>;
  return <Pill tone={filled ? "amber" : "rose"} icon={KeyRound}>{`${filled}/${total} credentials`}</Pill>;
};

export const CopyId = ({ value }) => {
  const toast = useToast();
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value).then(() => toast("Copied"), () => toast("Could not copy", "error"));
      }}
      className="group inline-flex max-w-full items-center gap-1 font-mono text-[11px] text-ink-400 hover:text-ink-700"
      title="Copy"
    >
      <span className="truncate">{value}</span>
      <Copy size={10} className="shrink-0 opacity-0 group-hover:opacity-100" aria-hidden />
    </button>
  );
};

/* ------------------------------------------------------------------ drawer */

export const Section = ({ title, icon: Icon, children, action }) => (
  <section className="border-t border-ink-150 px-6 py-5">
    <div className="mb-3 flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-ink-500">
        <Icon size={13} className="text-ink-400" aria-hidden />
        {title}
      </h3>
      {action}
    </div>
    {children}
  </section>
);

export const Count = ({ label, value, tone }) => (
  <div className="rounded-xl border border-ink-200/70 bg-white px-3 py-2.5">
    <p className={cx("text-[18px] font-semibold tabular-nums", tone === "rose" && value > 0 ? "text-rose-600" : "text-ink-900")}>{value.toLocaleString()}</p>
    <p className="text-[11.5px] text-ink-500">{label}</p>
  </div>
);
/* ------------------------------------------------------------------ plans */

const PER = { month: "/mo", year: "/yr", one_time: " once" };

/** "$29/mo", or "Free" for a zero price. */
export const priceText = (plan) => {
  if (!plan || plan.amount === undefined || plan.amount === null) return "";
  if (!plan.amount) return "Free";
  let money;
  try {
    money = new Intl.NumberFormat(undefined, { style: "currency", currency: plan.currency || "USD", maximumFractionDigits: plan.amount % 1 ? 2 : 0 }).format(plan.amount);
  } catch {
    money = `${plan.amount} ${plan.currency || ""}`.trim();
  }
  return `${money}${PER[plan.interval] || ""}`;
};

/** A plan's name, or a shortened id when nobody has named it yet. */
export const planName = (plan) => (plan?.name ? plan.name : plan?.planId ? `Plan …${plan.planId.slice(-6)}` : "");

/* ------------------------------------------------------------ detail panel */

/**
 * The slide-in panel each directory page opens a record in. Portalled to
 * <body>: the page wrapper animates with a transform, which would otherwise
 * make this "fixed" panel position against the wrapper and cut it off.
 */
export const Drawer = ({ onClose, label, header, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={label}>
      <button type="button" className="absolute inset-0 bg-ink-900/30 backdrop-blur-[1px]" onClick={onClose} aria-label="Close" />
      <aside className="relative flex h-full w-full max-w-[34rem] flex-col overflow-hidden bg-ink-50 shadow-2xl animate-fade-in">
        <div className="flex items-start gap-3 border-b border-ink-150 bg-white px-6 py-5">
          <div className="min-w-0 flex-1">{header}</div>
          <button type="button" onClick={onClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>,
    document.body,
  );
};

export const DrawerLoading = () => (
  <div className="space-y-3 p-6">
    {[0, 1, 2].map((k) => (
      <Skeleton key={k} className="h-24 w-full" rounded="rounded-xl" />
    ))}
  </div>
);

/**
 * Which record's panel is open, kept in the address as ?open=<id> — so a
 * reload keeps it open, and another page can link straight to a record.
 * Changing it leaves the page number and filters alone.
 */
export const useOpenParam = () => {
  const [params, setParams] = useSearchParams();
  const open = params.get("open") || null;
  const setOpen = useCallback(
    (id) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("open", id);
          else next.delete("open");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [open, setOpen];
};

/** Label / value pairs at the top of a panel. Empty values are left out. */
export const InfoGrid = ({ rows }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 bg-white px-6 py-5 text-[12.5px]">
    {rows
      .filter(([, v]) => v !== undefined && v !== null && v !== "" && v !== false)
      .map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[11px] font-medium text-ink-400">{k}</dt>
          <dd className="mt-0.5 break-words text-ink-800">{v}</dd>
        </div>
      ))}
  </dl>
);

/**
 * Whether the CRM details were fetched. Records start with just their id and
 * fill in once a working token reads them (see backend _utils/installDirectory.js).
 */
export const DetailsState = ({ record }) =>
  record?.lastSyncedAt ? null : (
    <Pill tone="amber" icon={RefreshCw} title={record?.syncError ? `Last attempt: ${record.syncError}` : "Not fetched yet"}>
      Details pending
    </Pill>
  );

/** A small row of app icons. */
export const AppStack = ({ apps = [], max = 4 }) => (
  <div className="flex items-center">
    {apps.slice(0, max).map((a, i) => (
      <span key={a.appId} title={a.appName} className={cx("rounded-md ring-2 ring-white", i > 0 && "-ml-1.5", a.active === false && "opacity-40")}>
        <AppIcon app={a} size="sm" className="!h-6 !w-6 !rounded-md !text-[9px]" />
      </span>
    ))}
    {apps.length > max && <span className="ml-1.5 text-[11.5px] text-ink-400">+{apps.length - max}</span>}
    {apps.length === 0 && <span className="text-[12px] text-ink-300">—</span>}
  </div>
);

/** Installs listed inside a panel, each linking to it on the Installs page. */
export const InstallList = ({ installs, showAccount = true }) =>
  installs.length === 0 ? (
    <p className="text-[13px] text-ink-500">No installs.</p>
  ) : (
    <ul className="divide-y divide-ink-150 overflow-hidden rounded-xl border border-ink-200/70 bg-white">
      {installs.map((i) => (
        <li key={i._id}>
          <Link to={`/installs?q=${encodeURIComponent(i.locationId || i.companyId || "")}`} className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-ink-50">
            <AppIcon app={i} size="sm" className="!h-7 !w-7 !rounded-md !text-[9px]" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-ink-800">{showAccount ? i.locationName || i.appName : i.appName}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                <StatusPill install={i} />
                {i.active && <CredentialsPill install={i} />}
                {i.active && <WebhookPill install={i} />}
                {i.active && !i.connected && <Pill tone="rose" icon={AlertTriangle}>No access token</Pill>}
              </div>
            </div>
            <ArrowRight size={13} className="shrink-0 text-ink-300" />
          </Link>
        </li>
      ))}
    </ul>
  );

/** A list of related records inside a panel, each opening on its own page. */
export const RecordLinks = ({ items, to, empty, render }) =>
  items.length === 0 ? (
    <p className="text-[13px] text-ink-500">{empty}</p>
  ) : (
    <ul className="space-y-1.5">
      {items.map((item) => (
        <li key={item._id}>
          <Link to={to(item)} className="flex items-center gap-2.5 rounded-lg border border-ink-200/70 bg-white px-3 py-2 text-[12.5px] hover:border-ink-300">
            {render(item)}
            <ArrowRight size={12} className="ml-auto shrink-0 text-ink-300" />
          </Link>
        </li>
      ))}
    </ul>
  );
